import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { IControl, IControlledMutator, IDisable, IMutator } from "sonobello.utilities.react";

import INoteTag from "../../../CustomerNotes/Types/INoteTag";
import { INoteTagContentBuilder } from "../../../CustomerNotes/Types/INoteTagContentBuilder";
import { INoteTagBuilder } from "../../../CustomerNotes/Types/NoteTagsConfiguration";

/**
 * @typeParam TNoteTagProps - The properties provided to each note tag template callback.
 */
export interface IAppointmentNoteCardProps<TNoteTagProps> extends IDisable, IMutator<string> {
  noteTagProps: TNoteTagProps;
}

/** Props for the {@link IAppointmentNoteCardConfig} custom note field component. */
export type IAppointmentNoteFieldProps = IControlledMutator<string> & IDisable;

/**
 * @typeParam TNoteTagProps - The properties provided to each note tag template callback.
 */
export interface IAppointmentNoteCardConfig<TNoteTagProps> {
  noteTagContentBuilder: INoteTagContentBuilder;
  noteTagConfig: INoteTagBuilder<TNoteTagProps>;
  NoteTags: React.FC<IControl<INoteTag[]>>;
  CustomNoteField: React.FC<IAppointmentNoteFieldProps>;
}

export const classNames = {
  conciseToggleButton: "AppointmentNoteCard_concise-toggle-button"
};

const AppointmentNoteCard = <TNoteTagProps,>({
  disabled,
  noteTagConfig,
  noteTagProps,
  noteTagContentBuilder,
  onChange,
  NoteTags,
  CustomNoteField
}: IAppointmentNoteCardProps<TNoteTagProps> & IAppointmentNoteCardConfig<TNoteTagProps>) => {
  const [tags, setTags] = useState<INoteTag[]>([]);
  const [showConciseTags, setShowConciseTags] = useState<boolean>(true);
  const [customNote, setCustomNote] = useState<string>("");
  const [appointmentNote, setAppointmentNote] = useState<string>("");

  useEffect(() => {
    setTags(noteTagConfig.getTags(noteTagProps));
  }, [noteTagConfig, noteTagProps]);

  useEffect(() => {
    const appointmentNote = `${noteTagContentBuilder.getNoteTagContent(tags)}${
      customNote ? ` ${customNote}` : ""
    }`.trim();
    onChange(appointmentNote);
    setAppointmentNote(appointmentNote);
  }, [customNote, noteTagContentBuilder, onChange, tags]);

  return (
    <Stack spacing={2}>
      <Box position="relative" minHeight={20} display="flex" alignItems="center">
        <Typography
          variant="h6"
          position="absolute"
          left="50%"
          sx={{ transform: "translateX(-50%)", width: "max-content" }}
        >
          Appointment Notes
        </Typography>
        <Box position="absolute" right={0}>
          <Tooltip title={showConciseTags ? "Show Text" : "Show Tags"}>
            <IconButton
              className={classNames.conciseToggleButton}
              size="small"
              onClick={() => setShowConciseTags(!showConciseTags)}
            >
              <SpeakerNotesIcon color="primary" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {showConciseTags ? (
        <NoteTags value={tags} />
      ) : (
        <Box sx={{ overflow: "auto", maxHeight: "180px", width: "100%" }}>
          <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
            {appointmentNote}
          </Typography>
        </Box>
      )}
      <CustomNoteField value={customNote} onChange={setCustomNote} disabled={disabled} />
    </Stack>
  );
};

export default AppointmentNoteCard;
